import React, { useState, useRef } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import { drawVectors, simulate } from '../lib/dispersion.js'
import toFixed from '../lib/toFixed.js'

const sources = [
  { name: 'Acceleration', value: .2, direction: 'radial' },
  { name: 'Aiming Error', value: .125, direction: 'radial' },
  { name: 'Bullet Imbalance', value: .215, direction: 'radial' },
  { name: 'Epicyclic Swerve', value: .01, direction: 'radial' },
  { name: 'Crosswind Jump', value: .03, direction: 'vertical' },
  { name: 'Wind Drift', value: .015, direction: 'horizontal' },
  { name: 'Velocity Variation', value: .02, direction: 'vertical' }
]

const getInitialSources = () => {
  return sources.map(source => ({ ...source }))
}

function DispersionSimulator() {
  const [sources, setSources] = useAutoSaveState('sources', getInitialSources())
  const [scale, setScale] = useAutoSaveState('scale', 300)
  const [allPoints, setAllPoints] = useState([])
  const [stats, setStats] = useState(null)
  const [simulations, setSimulations] = useAutoSaveState('simulations', 100)
  const [sampleSize, setSampleSize] = useAutoSaveState('sampleSize', 20)
  const [ preview, setPreview ] = useAutoSaveState('preview', null)
  const [simulationResult, setSimulationResult] = useState(null)
  const canvasRef = useRef(null)

  const clear = count => {
    const canvas = canvasRef.current
    canvas.height = canvas.offsetHeight
    canvas.width = canvas.offsetWidth
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setAllPoints([])
  }

  const draw = count => {
    const canvas = canvasRef.current
    canvas.height = canvas.offsetHeight
    canvas.width = canvas.offsetWidth
    const ctx = canvas.getContext('2d')
    const stats = drawVectors(canvas, ctx, sources, count, scale, allPoints)
    console.log(stats)
    setStats(stats)
    setAllPoints([ ...allPoints ])
    setPreview(`Avg Five Shot Group: ${toFixed(stats.averageFiveShotGroup, 3)}`)
    window.gtag('event', 'calculate-dispersion-simulator')
  }

  const runSimulation = async () => {
    const canvas = canvasRef.current
    canvas.height = canvas.offsetHeight
    canvas.width = canvas.offsetWidth
    const ctx = canvas.getContext('2d')
    const result = await simulate(sampleSize, simulations, sources, canvas, ctx, scale)
    setSimulationResult(result)
    window.gtag('event', 'calculate', { name: 'dispersion-simulator-power-analysis' })
    console.log(result)
  }

  console.log({ sources })
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center">
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" style={{ textAlign: 'center', paddingBottom: 20 }}>
            Dispersion Simulator
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={12}>
          <Paper sx={{ p : 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={2}><Button variant="contained" fullWidth onClick={() => draw(1)}>Draw</Button></Grid>
              <Grid item xs={2}><Button variant="contained" fullWidth onClick={() => draw(10)}>10x</Button></Grid>
              <Grid item xs={2}><Button variant="contained" fullWidth onClick={() => draw(100)}>100x</Button></Grid>
              <Grid item xs={2}><Button variant="contained" fullWidth onClick={() => draw(1000)}>1000x</Button></Grid>
              <Grid item xs={2}><Button variant="contained" fullWidth onClick={() => draw(100000)}>100000x</Button></Grid>
              <Grid item xs={2}><Button variant="contained" fullWidth onClick={() => clear()}>Clear</Button></Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h3" style={{ textAlign: 'center', paddingBottom: 20 }}>Variables</Typography>
            <Grid container spacing={2}>
              {sources.map((source, i) => <>
                <Grid item xs={1}>
                  <Checkbox
                    checked={source.test}
                    onChange={e => setSources(sources => {
                      sources[i].test = e.target.checked
                      return [ ...sources ]
                    })}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={source.name}
                    onChange={e => setSources(sources => {
                      sources[i].name = e.target.value
                      return [ ...sources ]
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">moa</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="SD"
                    value={source.value}
                    onChange={e => setSources(sources => {
                      sources[i].value = e.target.value
                      return [ ...sources ]
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">moa</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={3}>
                    <Select
                      fullWidth
                      value={source.direction}
                      label="Direction"
                      onChange={e => setSources(sources => {
                        sources[i].direction = e.target.value
                        return [ ...sources ]
                      })}
                    >
                      <MenuItem value="radial">Radial</MenuItem>
                      <MenuItem value="vertical">Vertical</MenuItem>
                      <MenuItem value="horizontal">Horizontal</MenuItem>
                    </Select>
                </Grid>
              </>)}
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    setSources([
                      ...sources,
                      { name: 'Error Source', value: '0', direction: 'radial' }
                    ])
                  }}
                >
                  <AddIcon />
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    setSources(sources.filter((v, i, a) => (i !== a.length - 1)))
                  }}
                >
                  <RemoveIcon />
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper sx={{ p: 1 }}>
            <Typography variant="h3" style={{ textAlign: 'center', paddingBottom: 20 }}>Results</Typography>
            <canvas ref={canvasRef} style={{ border: '1px solid #aaa', width: '100%', aspectRatio: '1 / 1' }}/>
            {stats && <Grid container spacing={2}>
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography style={{ textAlign: 'center' }} color="textSecondary">Standard Deviation</Typography>
                <Typography style={{ textAlign: 'center' }} variant="h6">{toFixed(stats.sd, 3)}</Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography style={{ textAlign: 'center' }} color="textSecondary">Mean Radius</Typography>
                <Typography style={{ textAlign: 'center' }} variant="h6">{toFixed(stats.averageDistance, 3)}</Typography>
              </Grid>
              <Grid item xs={1} />
              <Grid item xs={4}>
                <Typography style={{ textAlign: 'center' }} color="textSecondary">CEP 50</Typography>
                <Typography style={{ textAlign: 'center' }} variant="h6">{toFixed(stats.cep50, 3)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography style={{ textAlign: 'center' }} color="textSecondary">CEP 95</Typography>
                <Typography style={{ textAlign: 'center' }} variant="h6">{toFixed(stats.cep95, 3)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography style={{ textAlign: 'center' }} color="textSecondary">CEP 99</Typography>
                <Typography style={{ textAlign: 'center' }} variant="h6">{toFixed(stats.cep99, 3)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography style={{ textAlign: 'center' }} color="textSecondary">Min Five Shot Group</Typography>
                <Typography style={{ textAlign: 'center' }} variant="h6">{toFixed(stats.minFiveShot, 3)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography style={{ textAlign: 'center' }} color="textSecondary">Avg Five Shot Group</Typography>
                <Typography style={{ textAlign: 'center' }} variant="h6">{toFixed(stats.averageFiveShotGroup, 3)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography style={{ textAlign: 'center' }} color="textSecondary">Max Five Shot Group</Typography>
                <Typography style={{ textAlign: 'center' }} variant="h6">{toFixed(stats.maxFiveShot, 3)}</Typography>
              </Grid>
            </Grid>}
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 1 }}>
            <Typography variant="h3" style={{ textAlign: 'center', paddingBottom: 20 }}>Power Analysis</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Experiment Simulations"
                  value={simulations}
                  onChange={e => setSimulations(e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Shots Per Experiment"
                  value={sampleSize}
                  onChange={e => setSampleSize(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" fullWidth onClick={() => runSimulation()}>
                  Simulate
                </Button>
              </Grid>
              {simulationResult && <>
                <Grid item xs={12}>
                  <Typography variant="h3" style={{ textAlign: 'center', paddingTop: 20 }}>Mann Whitney U Test</Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell>p &lt; .01</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>p &lt; .05</TableCell>
                          <TableCell>p &lt; .1</TableCell>
                          <TableCell>p &lt; .2</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Chance of significant result</TableCell>
                          <TableCell>{simulationResult.passRate['.01']}</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>{simulationResult.passRate['.05']}</TableCell>
                          <TableCell>{simulationResult.passRate['.1']}</TableCell>
                          <TableCell>{simulationResult.passRate['.2']}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Chance of significant result</TableCell>
                          <TableCell>{simulationResult.replicationRate['.01']}</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>{simulationResult.replicationRate['.05']}</TableCell>
                          <TableCell>{simulationResult.replicationRate['.1']}</TableCell>
                          <TableCell>{simulationResult.replicationRate['.2']}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Chance of significant result</TableCell>
                          <TableCell>{simulationResult.errorRate['.01']}</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>{simulationResult.errorRate['.05']}</TableCell>
                          <TableCell>{simulationResult.errorRate['.1']}</TableCell>
                          <TableCell>{simulationResult.errorRate['.2']}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h3" style={{ textAlign: 'center', paddingTop: 20 }}>Five Shot Group T Test</Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell>p &lt; .01</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>p &lt; .05</TableCell>
                          <TableCell>p &lt; .1</TableCell>
                          <TableCell>p &lt; .2</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Chance of significant result</TableCell>
                          <TableCell>{simulationResult.fiveShotTTestPassRate['.01']}</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>{simulationResult.fiveShotTTestPassRate['.05']}</TableCell>
                          <TableCell>{simulationResult.fiveShotTTestPassRate['.1']}</TableCell>
                          <TableCell>{simulationResult.fiveShotTTestPassRate['.2']}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Chance of significant result</TableCell>
                          <TableCell>{simulationResult.fiveShotTTestReplicationRate['.01']}</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>{simulationResult.fiveShotTTestReplicationRate['.05']}</TableCell>
                          <TableCell>{simulationResult.fiveShotTTestReplicationRate['.1']}</TableCell>
                          <TableCell>{simulationResult.fiveShotTTestReplicationRate['.2']}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Chance of significant result</TableCell>
                          <TableCell>{simulationResult.fiveShotTTestErrorRate['.01']}</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>{simulationResult.fiveShotTTestErrorRate['.05']}</TableCell>
                          <TableCell>{simulationResult.fiveShotTTestErrorRate['.1']}</TableCell>
                          <TableCell>{simulationResult.fiveShotTTestErrorRate['.2']}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h3" style={{ textAlign: 'center', paddingTop: 20 }}>Vertical and Horizontal F Test</Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell>p &lt; .01</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>p &lt; .05</TableCell>
                          <TableCell>p &lt; .1</TableCell>
                          <TableCell>p &lt; .2</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Chance of significant result</TableCell>
                          <TableCell>{simulationResult.fTestPassRate['.01']}</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>{simulationResult.fTestPassRate['.05']}</TableCell>
                          <TableCell>{simulationResult.fTestPassRate['.1']}</TableCell>
                          <TableCell>{simulationResult.fTestPassRate['.2']}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Chance of significant result</TableCell>
                          <TableCell>{simulationResult.fTestReplicationRate['.01']}</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>{simulationResult.fTestReplicationRate['.05']}</TableCell>
                          <TableCell>{simulationResult.fTestReplicationRate['.1']}</TableCell>
                          <TableCell>{simulationResult.fTestReplicationRate['.2']}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Chance of significant result</TableCell>
                          <TableCell>{simulationResult.fTestErrorRate['.01']}</TableCell>
                          <TableCell style={{ backgroundColor: '#ccc' }}>{simulationResult.fTestErrorRate['.05']}</TableCell>
                          <TableCell>{simulationResult.fTestErrorRate['.1']}</TableCell>
                          <TableCell>{simulationResult.fTestErrorRate['.2']}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h3" style={{ textAlign: 'center', paddingTop: 20 }}>Five Shot Group Avg ES</Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Change of correct result</TableCell>
                          <TableCell>{simulationResult.fiveShotPassRate}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Change of replication</TableCell>
                          <TableCell>{simulationResult.fiveShotReplicationRate}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Change of wrong result</TableCell>
                          <TableCell>{simulationResult.fiveShotErrorRate}</TableCell>
                        </TableRow>
                      </TableHead>
                    </Table>
                  </TableContainer>
                </Grid>
              </>}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default function WrappedDispersionSimulator() {
  return (
    <AutoSaveProvider>
      <DispersionSimulator />
    </AutoSaveProvider>
  )
}
